// src/pages/NewAskKural/components/InlineQuantCitationTag/index.tsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import { useCitationLoaderState } from "../../hooks/useCitationLoaderState";
import { IQuantCitationRef, ReferenceDrawerVisibilityType } from "../../models/interfaces";
import { setActiveCitationReferences, resetActiveWidgetData, setIsAskKuralSidebarOpen, setReferenceDrawerVisibility } from "../../states/askKuralSlice";
import { setCountSelectedQuote, setCountWidgetOpen } from "../../states/countWidgetSlice";
import { transformFePayloadToChartResponse } from "../../transformer";
import DynamicChartRender from "src/pages/Insights/components/QuantDashboard/Charts/DynamicChartRender";
import TagLoader from "public/icon/TagLoader.svg";
import { useTranslation } from "react-i18next";
import "./style.css";
import { ChartRenderedFromType } from "src/pages/Insights/models/interfaces";

interface IInlineQuantCitationTagProps {
	citationId: string;
	studyType: string;
	messageId: string;
	quantCitationRef: IQuantCitationRef | null;
	onCitationClick?: (citationId: string, studyType: string, references: any[], messageId: string, scrollUniqueId?: string) => void;
	handleInlineWidgetScroll?: (scrollId: string) => void;
	isReferenceLoading?: boolean;
	isReferenceTerminal?: boolean;
	/** Per-message completion override for the citation loader (see useCitationLoaderState). */
	isProcessComplete?: boolean;
	/** When true, auto-opens the drawer on mount for $inline citations */
	isInline?: boolean;
}

const InlineQuantCitationTag: React.FC<IInlineQuantCitationTagProps> = ({
	citationId,
	studyType,
	messageId,
	quantCitationRef,
	onCitationClick,
	handleInlineWidgetScroll,
	isReferenceLoading = false,
	isReferenceTerminal = false,
	isProcessComplete: isMessageProcessComplete,
	isInline = false,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { activeCitationReferences, isProcessComplete } = useSelector((state: RootState) => state.askKuralSlice);

	const scrollUniqueIdRef = useRef(`quant-${citationId}-${crypto.randomUUID()}`);
	const scrollUniqueId = scrollUniqueIdRef.current;

	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [isChartView, setIsChartView] = useState<boolean>(true);

	const hasData = !!(quantCitationRef?.fe_payload?.chart_type && quantCitationRef?.fe_payload?.data);

	const isSelected = useMemo(() => activeCitationReferences?.citationId === citationId, [activeCitationReferences, citationId]);

	const chartTitle = useMemo(() => quantCitationRef?.fe_payload?.chart_title || "Chart", [quantCitationRef]);

	const shouldShowLoader = useCitationLoaderState({
		hasData,
		isReferenceLoading,
		isReferenceTerminal,
		isProcessComplete: isMessageProcessComplete,
	});

	// Memoize chart transformation — only computed when expanded and data exists
	const chartsResponse = useMemo(() => {
		if (!isExpanded || !quantCitationRef?.fe_payload) return [];
		const chartResponse = transformFePayloadToChartResponse(quantCitationRef.fe_payload);
		return chartResponse ? [chartResponse] : [];
	}, [isExpanded, quantCitationRef]);

	const handleToggleExpand = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			e.stopPropagation();
			setIsExpanded((prev) => !prev);
			handleInlineWidgetScroll?.(scrollUniqueId);
		},
		[scrollUniqueId, handleInlineWidgetScroll],
	);

	const handleViewDetails = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			if (!quantCitationRef?.fe_payload) return;

			handleInlineWidgetScroll?.(scrollUniqueId);

			dispatch(
				setActiveCitationReferences({
					citationId,
					studyType: "quant",
					messageId,
					references: [quantCitationRef],
				}),
			);

			dispatch(resetActiveWidgetData());
			dispatch(setCountSelectedQuote(null));
			dispatch(setCountWidgetOpen(false));
			dispatch(setIsAskKuralSidebarOpen(false));
			onCitationClick?.(citationId, "quant", [quantCitationRef], messageId, scrollUniqueId);
			dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.shiftedExpanded));

			// Collapse the inline card — drawer is now the detail view
			setIsExpanded(false);
		},
		[citationId, quantCitationRef, messageId, scrollUniqueId, dispatch, onCitationClick, handleInlineWidgetScroll],
	);

	const handleTagKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault();
				handleToggleExpand(e);
			}
		},
		[handleToggleExpand],
	);

	// Gate auto-expand on isProcessComplete so the inline chart is not mounted
	// on every streamed token during active streaming. During streaming
	// (isProcessComplete === false) the component stays as a collapsed pill —
	// no DynamicChartRender is mounted, eliminating per-token re-measure jitter.
	// Once streaming ends (isProcessComplete === true) the effect fires once and
	// expands the chart. History-loaded chats already have isProcessComplete===true
	// so they behave identically to the previous behaviour.
	useEffect(() => {
		if (isInline && hasData && !isExpanded && isProcessComplete) {
			setIsExpanded(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInline, hasData, isProcessComplete]);

	// Render nothing when terminal with no chart data (no error pill).
	if (!hasData && !shouldShowLoader) {
		return null;
	}

	// Loading state
	if (!hasData && shouldShowLoader) {
		return (
			<span className="inline-quant-citation-tag-wrapper inline-flex items-center inline-quant-citation-loading">
				<TagLoader className="spinning-loader" />
			</span>
		);
	}

	// Expanded inline card
	if (isExpanded) {
		return (
			<div id={scrollUniqueId} className={`inline-quant-citation-expanded ${isSelected ? "selected" : ""}`}>
				<div className="inline-quant-citation-expanded-header">
					<div className="inline-quant-citation-expanded-title">
						<i className="material-symbols-outlined text-base-1 mt-0.5 text-[--primary-color-2]">bar_chart_4_bars</i>
						<span className="inline-quant-citation-chart-title text-base-1" title={chartTitle}>
							{chartTitle}
						</span>
					</div>
					<span
						className="inline-quant-citation-collapse-btn"
						onClick={handleToggleExpand}
						onKeyDown={handleTagKeyDown}
						role="button"
						tabIndex={0}
						aria-label={t("Collapse chart")}>
						<i className="material-symbols-outlined text-base">expand_less</i>
					</span>
				</div>
				<div className="inline-quant-citation-expanded-chart">
					{chartsResponse.length > 0 && (
						<DynamicChartRender
							chartsResponse={chartsResponse}
							isChartView={isChartView}
							setIsChartView={setIsChartView}
							setChartData={() => {}}
							setIsChartLoading={() => {}}
							isAISummaryAvailable={false}
							renderedFrom={ChartRenderedFromType.AskKuralInline}
						/>
					)}
				</div>
				<div className="inline-quant-citation-expanded-footer">
					<span
						className="inline-quant-citation-view-details text-base"
						onClick={handleViewDetails}
						role="button"
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								handleViewDetails(e as unknown as React.MouseEvent);
							}
						}}>
						{t("View Details")}
					</span>
				</div>
			</div>
		);
	}

	// ─── Non-inline pill mode ───
	// Regular (non-inline) quant citations render as a compact pill
	// that directly opens the reference drawer instead of expanding a chart.
	if (!isInline) {
		return (
			<span
				id={scrollUniqueId}
				className={`inline-quant-citation-tag ${isSelected ? "selected" : ""}`}
				onClick={handleViewDetails}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						handleViewDetails(e as unknown as React.MouseEvent);
					}
				}}
				role="button"
				tabIndex={0}
				aria-label={`${t("View chart")}: ${chartTitle}`}>
				<i className="material-symbols-outlined text-base">bar_chart</i>
				<i className="material-symbols-outlined text-base">chevron_right</i>
			</span>
		);
	}

	// Collapsed tag (default) — only reachable for inline citations
	// ─── Inline collapsed tag (expandable mode) ───
	return (
		<div
			id={scrollUniqueId}
			className={`inline-quant-citation-collapsed ${isSelected ? "selected" : ""}`}
			onClick={handleToggleExpand}
			onKeyDown={handleTagKeyDown}
			role="button"
			tabIndex={0}
			aria-label={`View chart: ${chartTitle}`}>
			<div className="inline-quant-citation-collapsed-left">
				<i className="material-symbols-outlined text-base text-[--secondary-color]">bar_chart</i>
				<span className="inline-quant-citation-collapsed-title" title={chartTitle}>
					{chartTitle}
				</span>
			</div>
			<i className="material-symbols-outlined text-base text-[--secondary-color]">expand_more</i>
		</div>
	);
};

export default InlineQuantCitationTag;
